import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _createForOfIteratorHelper(r, e) { var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (!t) { if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) { t && (r = t); var _n = 0, F = function F() {}; return { s: F, n: function n() { return _n >= r.length ? { done: !0 } : { done: !1, value: r[_n++] }; }, e: function e(r) { throw r; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var o, a = !0, u = !1; return { s: function s() { t = t.call(r); }, n: function n() { var r = t.next(); return a = r.done, r; }, e: function e(r) { u = !0, o = r; }, f: function f() { try { a || null == t.return || t.return(); } finally { if (u) throw o; } } }; }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
import Bowser from 'bowser-ultralight';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { PeriodTracking } from './insm-period';
import { LongAnimationFrameMeasurer } from './session-measurers/LongAnimationFrameMeasurer';

/**
 * Only intended for internal use.
 *
 * Exported for consumers who may require the type.
 *
 * Note: Events are not reliably fired from mobile browsers (ie. when a browser is closed when not in use)
 */
export var INSMSession = /*#__PURE__*/function () {
  function INSMSession(experienceKey, experienceProperties, insm) {
    _classCallCheck(this, INSMSession);
    _defineProperty(this, "startedAt", performance.now());
    _defineProperty(this, "pageLoadTime", null);
    _defineProperty(this, "running", true);
    _defineProperty(this, "addedProperties", []);
    _defineProperty(this, "runningFeatures", new Set());
    this.experienceKey = experienceKey;
    this.experienceProperties = experienceProperties;
    this.insm = insm;
    this.periodTracking = new PeriodTracking(this);
    this.longAnimationFrameMeasurer = new LongAnimationFrameMeasurer({
      initial: this.experienceProperties.initial,
      limit: 3,
      insmSession: this,
      reportingThreshold: 500
    });
  }

  /**
   * Completes the page load timing. This is called automatically when ending a heavy task
   * with the key 'PageLoad'.
   */
  return _createClass(INSMSession, [{
    key: "completePageLoad",
    value: function completePageLoad() {
      this.pageLoadTime = performance.now() - this.startedAt;
    }
  }, {
    key: "updateExperienceKey",
    value: function updateExperienceKey(experienceKey) {
      this.experienceKey = experienceKey;
    }

    /**
     * Adds a feature to the currently running session
     */
  }, {
    key: "startFeature",
    value: function startFeature(featureName) {
      var _this$periodTracking;
      this.runningFeatures.add(featureName);
      (_this$periodTracking = this.periodTracking) === null || _this$periodTracking === void 0 || _this$periodTracking.startFeature(featureName);
    }

    /**
     * Ends a features usage in the currently running session
     */
  }, {
    key: "endFeature",
    value: function endFeature(featureName) {
      this.runningFeatures.delete(featureName);
    }

    /**
     * Returns details on the current session.
     */
  }, {
    key: "details",
    get: function get() {
      return {
        experienceKey: this.experienceKey,
        experienceProperties: this.experienceProperties,
        paused: this.periodTracking.pauses.size > 0,
        periodState: this.periodTracking.state,
        /**
         * The only scenario where this value should return false is when
         * the experience has been stopped early.
         */
        running: this.running
      };
    }

    /**
     * This api takes either a static single-level key-value object, or callbacks which return the same and
     * will be evaluated on session end.
     *
     * When ending a session, all properties received via this api are merged, in order, into the resulting
     * insm event’s properties; last write wins.
     *
     * Callback values are evaluated at session end.
     *
     * For example, for the following
     *
     * ```ts
     * insm.experience.addProperties({ one: 1, two: 2 });
     * insm.experience.addProperties(() => ({ one: 'one' }));
     * insm.experience.addProperties({ three: 3 });
     * ```
     *
     * The resulting added properties will be
     *
     * ```ts
     * { one: 'one', two: 2, three: 3 }
     * ```
     */
  }, {
    key: "addProperties",
    value: function addProperties(propertiesToAdd) {
      this.addedProperties.push(propertiesToAdd);
    }

    /**
     * In some scenarios (ie. when a page error boundary is hit), you will want to exit early.
     * This is api supports these scenarios
     *
     * ```ts
     * insm.stopEarly(reasonKey: string, description: string);
     * ```
     *
     * Sessions closed early are identifiable by their end details
     * `"endDetails": { stoppedBy: "early-stop", reasonKey, description }`.
     *
     * **Note**: The session is ended as soon as this is called, and any `addProperties` handlers will
     * called immediately.
     */
  }, {
    key: "earlyStop",
    value: function earlyStop(reason, description) {
      this.end({
        stoppedBy: 'early-stop',
        reason: reason,
        description: description
      });
    }
  }, {
    key: "end",
    value: function end(endDetails) {
      var _periodResults$active, _periodResults$active2, _periodResults$active3, _periodResults$active4, _this$insm$analyticsW;
      if (this.running === false) {
        // If an experience has already been ended -- don't repeat the ending
        return;
      }
      this.running = false;
      var endedAt = performance.now();
      var duration = endedAt - this.startedAt;
      var evaluatedAddedProperties = {};
      var _iterator = _createForOfIteratorHelper(this.addedProperties),
        _step;
      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var addedProperty = _step.value;
          if (typeof addedProperty === 'function') {
            Object.assign(evaluatedAddedProperties, addedProperty());
          } else {
            Object.assign(evaluatedAddedProperties, addedProperty);
          }
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }
      var periodResults = this.periodTracking.endResults;

      /**
       * This event ends up as "insm measured"
       */
      var operationalEvent = {
        actionSubject: 'insm',
        action: 'measured',
        attributes: _objectSpread(_objectSpread(_objectSpread({}, evaluatedAddedProperties), {}, {
          experienceKey: this.experienceKey,
          initial: this.experienceProperties.initial,
          contentId: this.experienceProperties.contentId,
          timing: {
            startedAt: this.startedAt,
            // Note: this will not match up with the periods sum of durations, as it includes
            // time paused when heavy tasks were running, and/or the page is in the background.
            duration: duration
          },
          periods: periodResults,
          endDetails: endDetails
        }, expValEquals('cc_editor_insm_outlier_events', 'cohort', 'test') ? {
          longScripts: this.longAnimationFrameMeasurer.current,
          pageLoadTime: this.pageLoadTime,
          deviceDetails: getDeviceDetails()
        } : {}), {}, {
          // these health attributes drive our SLOs
          healthAFPS: (_periodResults$active = periodResults.active.measurements.afps) !== null && _periodResults$active !== void 0 && _periodResults$active.average ? ((_periodResults$active2 = periodResults.active.measurements.afps) === null || _periodResults$active2 === void 0 ? void 0 : _periodResults$active2.average) >= 50 : undefined,
          healthINP: (_periodResults$active3 = periodResults.active.measurements.inp) !== null && _periodResults$active3 !== void 0 && _periodResults$active3.average ? ((_periodResults$active4 = periodResults.active.measurements.inp) === null || _periodResults$active4 === void 0 ? void 0 : _periodResults$active4.average) >= 200 : undefined
        }),
        highPriority: true,
        tags: ['editor'],
        source: 'unknown'
      };
      (_this$insm$analyticsW = this.insm.analyticsWebClient) === null || _this$insm$analyticsW === void 0 || _this$insm$analyticsW.sendOperationalEvent(operationalEvent);
      this.longAnimationFrameMeasurer.cleanup();
    }
  }]);
}();

// Functionality vendored from UFO
function getDeviceDetails() {
  var _navigator$connection, _navigator$connection2, _navigator$connection3;
  var telemetry = {};

  // /platform/packages/data/ufo-internal/src/core/publisher/plugins/browser.ts
  if (Bowser.getParser) {
    var _ref;
    var browser = Bowser.getParser(((_ref = typeof window !== 'undefined' && !!window ? window : undefined) === null || _ref === void 0 || (_ref = _ref.navigator) === null || _ref === void 0 ? void 0 : _ref.userAgent) || '');
    Object.assign(telemetry, {
      'event:browser:name': browser.getBrowserName(),
      'event:browser:version': browser.getBrowserVersion()
    });
  }

  // /platform/packages/data/ufo-internal/src/core/publisher/plugins/cpus.ts
  Object.assign(telemetry, {
    'event:cpus': navigator.hardwareConcurrency
  });

  // /platform/packages/data/ufo-internal/src/core/publisher/plugins/memory.ts
  // @ts-ignore: deviceMemory is exposed in some browsers
  Object.assign(telemetry, {
    'event:memory': navigator.deviceMemory
  });

  // /platform/packages/data/ufo-internal/src/core/publisher/plugins/network.ts
  Object.assign(telemetry, {
    // Network
    // @ts-ignore: connection is available in some browsers
    // eslint-disable-next-line compat/compat
    'event:network:effectiveType': (_navigator$connection = navigator.connection) === null || _navigator$connection === void 0 ? void 0 : _navigator$connection.effectiveType,
    // @ts-ignore: connection is available in some browsers
    // eslint-disable-next-line compat/compat
    'event:network:rtt': (_navigator$connection2 = navigator.connection) === null || _navigator$connection2 === void 0 ? void 0 : _navigator$connection2.rtt,
    // @ts-ignore: connection is available in some browsers
    // eslint-disable-next-line compat/compat
    'event:network:downlink': (_navigator$connection3 = navigator.connection) === null || _navigator$connection3 === void 0 ? void 0 : _navigator$connection3.downlink
  });
  return telemetry;
}